import { Box, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import { getSwissPostOrdersRequest } from 'redux/reducers/shipments/reducer';
import { getShipmentsOrders, getShipmentsOrdersIsLoad } from 'redux/reducers/shipments/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { TSwissPostOrder } from 'redux/reducers/shipments/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import {
	getFiltersFromNavigateParams,
	getOrderByFromNavigateParams,
	notFixedStatusFilterBuilder,
} from 'utils/FilterParams';
import { SHIPMENTS_ACTIONS, shipmentsStructure } from './shipments-structure';

export const Shipments: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const tableStructure = shipmentsStructure as TTableStructure<keyof TSwissPostOrder>[];

	const shipments = useSelector(getShipmentsOrders);
	const shipmentsLoader = useSelector(getShipmentsOrdersIsLoad);

	const [prevSearch, setPrevSearch] = useState(navigateParams?.search);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(tableStructure, navigateParams, {
		customFilters: { status: notFixedStatusFilterBuilder },
	});

	const dispatchGetShipmentsOrders = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString =
			(searchStringRaw?.length ?? 0) > 2 || (searchStringRaw?.length ?? 0) === 0
				? searchStringRaw
				: '';
		dispatch(
			getSwissPostOrdersRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				filters: navigateParamsWithoutPage,
				searchString,
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
			}),
		);
	};

	useEffect(() => {
		const prevParams = queryString.parse(prevSearch as string);
		const otherPrevParams = Object.entries(prevParams).filter(([key]) => key !== 'search');
		const otherPrevParamsString = otherPrevParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');
		const currentParams = queryString.parse(location.search);
		const otherCurrentParams = Object.entries(currentParams).filter(([key]) => key !== 'search');
		const otherCurrentParamsString = otherCurrentParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');

		if (
			(prevParams.search !== currentParams.search &&
				((currentParams.search as string)?.length ?? 0) > 2) ||
			((prevParams.search as string)?.length ?? 0) === 0 ||
			otherPrevParamsString !== otherCurrentParamsString
		) {
			dispatchGetShipmentsOrders();
		}
		setPrevSearch(location.search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleLink = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/shipment/${id}`);
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		const order = shipments?.data?.find((item) => item.id === id);
		if (!order) return;

		if (typeOfDialog === SHIPMENTS_ACTIONS.TRACKING) {
			if (order.tracking) window.open(order.tracking, '_blank');
			return;
		}
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/shipment/${order.id}`);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TSwissPostOrder>
				tableStructure={tableStructure}
				items={shipments}
				itemsIsLoading={shipmentsLoader}
				handleAction={handleAction}
				handleLink={handleLink}
				minWidth={1200}
				name="order"
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						p: '2px 4px',
					}}
				>
					<Typography
						variant="h1"
						color="primary"
						sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', ml: 1 }}
					>
						Shipments
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
